import { globalRef, type Maybe } from '@amazon/vinyl-util'
import type {
    ClickContext,
    MetricsInteractionType,
    MetricsPage,
    RoutedPageDetails,
} from '@/metrics/uiMetrics'
import { inputInteraction, pressedElement } from '@/metrics/interactionInput'
import { itemClicksAt } from '@/metrics/itemClicks'

// The page the customer is on, kept apart from the uploader so the app shell can name the
// page a click happened on without loading the metrics chunk.

export interface RoutedPage {
    readonly view: Element
    readonly page: MetricsPage
    readonly awaitsDetails: boolean
}

export interface UiMetricsState {
    current: Maybe<MetricsPage>
    routed: Maybe<RoutedPage>
    /** The routed view whose own page view has been reported. */
    reportedView: Maybe<Element>
    readonly described: WeakMap<Element, RoutedPageDetails>
    /** In-page views waiting on their routed page's own view. */
    readonly pendingViews: WeakMap<Element, MetricsPage[]>
    /** Page views sent once a hidden tab is shown again. */
    readonly hiddenViews: Array<() => void>
    /** The window's orientation as of the last page view. */
    landscape: Maybe<boolean>
}

export const uiMetricsStateRef = globalRef<UiMetricsState>(() => ({
    current: null,
    routed: null,
    reportedView: null,
    described: new WeakMap(),
    pendingViews: new WeakMap(),
    hiddenViews: [],
    landscape: null,
}))

/** Where, when and by which input a UI event happened, read off the browser as it is reported. */
export interface UiEventContext {
    /** Epoch milliseconds. */
    readonly timestamp: number
    readonly url: string
    readonly viewType: 'LANDSCAPE' | 'PORTRAIT'
    /** The navigation's `?ref=` tag. */
    readonly refMarker: Maybe<string>
    /** The page this event happened on when the event names none. */
    readonly current: Maybe<MetricsPage>
    readonly interactionType: MetricsInteractionType
    readonly loadTimeMilliseconds?: Maybe<number>
    /** What the list item the customer pressed adds to a click. */
    readonly item?: Maybe<ClickContext>
}

export function uiEventContext(): UiEventContext {
    const url = new URL(location.href)
    return {
        timestamp: Date.now(),
        url: url.href,
        viewType: window.innerWidth > window.innerHeight ? 'LANDSCAPE' : 'PORTRAIT',
        refMarker: url.searchParams.get('ref'),
        current: uiMetricsStateRef.value.current,
        interactionType: inputInteraction(),
        item: itemClicksAt(pressedElement()),
    }
}
